import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Spinner,
  Text,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp, FiFileText } from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';

/**
 * "Show transcript" expander for AI call-summary notes.
 *
 * Transcripts are stored once on crm_dialpad_calls.transcript (never embedded
 * in activity rows), so this lazy-fetches the raw transcript the first time
 * the user expands it. Looks up by the call row uuid (metadata.callRowId),
 * falling back to the Dialpad call id.
 */
export default function TranscriptDisclosure({ callRowId, dialpadCallId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [error, setError] = useState(null);

  const transcriptBg = useColorModeValue('gray.50', 'gray.800');
  const transcriptBorder = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  const fetchTranscript = async () => {
    if (!isSupabaseConfigured()) {
      setError('Transcript store unavailable');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('crm_dialpad_calls').select('transcript');
      if (callRowId) {
        query = query.eq('id', callRowId);
      } else if (dialpadCallId) {
        query = query.eq('dialpad_call_id', String(dialpadCallId));
      } else {
        setError('No call reference on this note');
        setLoading(false);
        return;
      }
      const { data, error: fetchError } = await query.limit(1);
      if (fetchError) throw fetchError;
      const text = data?.[0]?.transcript;
      if (text && text.length > 0) {
        setTranscript(text);
      } else {
        setError('Transcript not found for this call');
      }
    } catch (err) {
      console.error('Error fetching call transcript:', err);
      setError('Could not load the transcript. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next && !transcript && !loading) {
      fetchTranscript();
    }
  };

  if (!callRowId && !dialpadCallId) return null;

  return (
    <Box mt={1}>
      <Button
        size="xs"
        variant="ghost"
        colorScheme="cyan"
        leftIcon={isOpen ? <FiChevronUp /> : <FiChevronDown />}
        onClick={handleToggle}
        alignSelf="flex-start"
      >
        {isOpen ? 'Hide transcript' : 'Show transcript'}
      </Button>

      <Collapse in={isOpen} animateOpacity>
        <Box
          mt={2}
          p={3}
          bg={transcriptBg}
          borderRadius="md"
          borderWidth="1px"
          borderColor={transcriptBorder}
          maxH="300px"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { background: '#A0AEC0', borderRadius: '3px' },
          }}
        >
          {loading && (
            <HStack spacing={2} py={2}>
              <Spinner size="xs" />
              <Text fontSize="xs" color="gray.500">Loading transcript…</Text>
            </HStack>
          )}
          {!loading && error && (
            <Text fontSize="xs" color="red.400">{error}</Text>
          )}
          {!loading && !error && transcript && (
            <>
              <HStack spacing={1} mb={2}>
                <Icon as={FiFileText} boxSize={3} color="cyan.600" />
                <Text fontSize="2xs" fontWeight="semibold" color="cyan.600" textTransform="uppercase">
                  Full transcript
                </Text>
              </HStack>
              <Text fontSize="xs" whiteSpace="pre-wrap" color={textColor}>
                {transcript}
              </Text>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
